import { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newStaff, setNewStaff] = useState({ name: '', email: '', role: 'agent' });
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const { staff: me } = useAuth();

  const fetchStaff = () => {
    api.get('/staff').then(({ data }) => { setStaff(data.staff || []); setLoading(false); });
  };

  useEffect(() => { fetchStaff(); }, []);

  const addStaff = async () => {
    if (!newStaff.name || !newStaff.email) return alert('Name and email required');
    setSaving(true);
    try {
      const { data } = await api.post('/staff', newStaff);
      setCreated(data);
      setNewStaff({ name: '', email: '', role: 'agent' });
      setShowAdd(false);
      fetchStaff();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add staff');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, current) => {
    if (!window.confirm(`${current ? 'Deactivate' : 'Reactivate'} this staff member?`)) return;
    try {
      await api.patch(`/staff/${id}`, { is_active: !current });
    } catch (err) {
      alert(err.response?.data?.error || `Failed to ${current ? 'deactivate' : 'reactivate'} staff member`);
    } finally {
      fetchStaff();
    }
  };

  if (me?.role !== 'admin') {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#c00' }}>Admin access required</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600' }}>Staff members</h2>
        <button onClick={() => setShowAdd(true)}
          style={{ padding: '8px 16px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
          + Add staff member
        </button>
      </div>

      {created && (
        <div style={{ background: '#EAF3DE', border: '1px solid #97C459', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', fontSize: '13px' }}>
          <strong>Staff member created.</strong> They sign in with Google as <strong>{created.staff.email}</strong>. There is no password; a welcome email with the login link is on its way.
          <button onClick={() => setCreated(null)} style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', color: '#555', fontSize: '12px' }}>Dismiss</button>
        </div>
      )}

      {showAdd && (
        <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '14px' }}>Add new staff member</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '10px', alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Full name</label>
              <input value={newStaff.name} onChange={e => setNewStaff(p => ({ ...p, name: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Google account (@tapgp.co.uk)</label>
              <input type="email" placeholder="name@tapgp.co.uk" value={newStaff.email} onChange={e => setNewStaff(p => ({ ...p, email: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Role</label>
              <select value={newStaff.role} onChange={e => setNewStaff(p => ({ ...p, role: e.target.value }))}
                style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}>
                <option value="agent">Agent</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={addStaff} disabled={saving}
                style={{ padding: '8px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
                {saving ? 'Adding...' : 'Add'}
              </button>
              <button onClick={() => setShowAdd(false)}
                style={{ padding: '8px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Loading...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: '500', color: '#666', fontSize: '12px' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: '500', color: '#666', fontSize: '12px' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: '500', color: '#666', fontSize: '12px' }}>Role</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: '500', color: '#666', fontSize: '12px' }}>Last login</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: '500', color: '#666', fontSize: '12px' }}>Status</th>
                <th style={{ padding: '12px 16px' }}></th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s, i) => (
                <tr key={s.id} style={{ borderBottom: i < staff.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                  <td style={{ padding: '12px 16px', fontWeight: '500' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#CECBF6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', color: '#3C3489' }}>
                        {s.name.slice(0, 2).toUpperCase()}
                      </div>
                      {s.name} {s.id === me?.id && <span style={{ fontSize: '11px', color: '#999' }}>(you)</span>}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', color: '#555' }}>{s.email}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500', background: s.role === 'admin' ? '#CECBF6' : '#f0f0f0', color: s.role === 'admin' ? '#3C3489' : '#555' }}>
                      {s.role}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', color: '#888', fontSize: '13px' }}>
                    {s.last_login ? new Date(s.last_login).toLocaleDateString('en-GB') : 'Never'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500', background: s.is_active ? '#EAF3DE' : '#f5f5f5', color: s.is_active ? '#27500A' : '#999' }}>
                      {s.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {s.id !== me?.id && (
                      <button onClick={() => toggleActive(s.id, s.is_active)}
                        style={{ fontSize: '12px', padding: '4px 10px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', background: '#fff', color: s.is_active ? '#c00' : '#27500A' }}>
                        {s.is_active ? 'Deactivate' : 'Reactivate'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
